import Endpoints from 'constants/endpoints';
import focusBearApi from 'services/axios-config';
import {
  resolveDayPlanImportMimeType,
  validateDayPlanImportFile
} from './file-validation';

/**
 * Day Plan calendar file import (private R2): the authenticated
 * `POST /day-plan/import/file/generate-upload-url` request goes through
 * `focusBearApi` (JWT). BE returns `{ uploadUrl, fileKey }`; the client then
 * PUTs bytes to `uploadUrl` without the app JWT (bare XHR to R2).
 */

/**
 * An expected, user-facing upload failure (invalid/oversized file, or the
 * upload being rejected). `message` is an i18n key. These are shown to the user
 * and must NOT be sent to error monitoring — only genuinely unexpected errors
 * should be captured.
 */
export class DayPlanFileUploadError extends Error {
  constructor(messageKey: string) {
    super(messageKey);
    this.name = 'DayPlanFileUploadError';
  }
}

/** Canonical BE shape; snake_case aliases accepted in the parser. */
interface GenerateFileUploadUrlResponse {
  uploadUrl?: string;
  fileKey?: string;
}

const parseGenerateResponse = (
  raw: Record<string, unknown>
): GenerateFileUploadUrlResponse => {
  const uploadUrl =
    (typeof raw.uploadUrl === 'string' && raw.uploadUrl) ||
    (typeof raw.upload_url === 'string' && raw.upload_url) ||
    undefined;
  const fileKey =
    (typeof raw.fileKey === 'string' && raw.fileKey) ||
    (typeof raw.file_key === 'string' && raw.file_key) ||
    (typeof raw.key === 'string' && raw.key) ||
    undefined;
  return { uploadUrl, fileKey };
};

const putToSignedUrl = (
  uploadUrl: string,
  file: File,
  contentType: string
): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new DayPlanFileUploadError('image_upload.upload_failed'));
    };
    xhr.onerror = () => reject(new Error('image_upload.upload_failed'));
    xhr.onabort = () => reject(new Error('image_upload.upload_failed'));
    xhr.send(file);
  });

/**
 * Validate + upload a calendar file to R2; returns `fileKey` for
 * `POST /day-plan/import/file` `{ key, client_datetime_iso, ... }`.
 */
export const uploadDayPlanFile = async (file: File): Promise<string> => {
  const validation = await validateDayPlanImportFile(file);
  if (!validation.valid) {
    throw new DayPlanFileUploadError(
      validation.error ?? 'file_validation.invalid_type'
    );
  }

  const contentType = resolveDayPlanImportMimeType(file);
  const { data } = await focusBearApi.post(Endpoints.DAY_PLAN_FILE_UPLOAD_URL, {
    content_type: contentType
  });

  const { uploadUrl, fileKey } = parseGenerateResponse(
    (data ?? {}) as Record<string, unknown>
  );
  if (!uploadUrl || !fileKey) {
    throw new DayPlanFileUploadError('image_upload.failed_to_get_url');
  }

  await putToSignedUrl(uploadUrl, file, contentType);
  return fileKey;
};
